import React, { ChangeEvent } from 'react';

import { Box, FormControlLabel, Link, Stack, Tooltip } from '@mui/material';
import { Form, Formik, FormikHelpers } from 'formik';
import { Paperclip2, Record, TickCircle } from 'iconsax-reactjs';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { TooltipSx } from '../../../SignIn/styles';
import {
  ButtonStyled,
  CheckboxStyled,
  ContentWrapperStyled,
  DescriptionStyled,
  FormFieldContainerStyled,
  FormSectionWrapperStyled,
  FormTextLabelStyled,
  FormWrapperStyled,
  InfoTextStyled,
  TextFieldStyled,
  TitleStyled,
  UploadButtonStyled,
} from '../../styles';
import { IRegistrationForm } from '../../types';
import ChosenFile from '../ChosenFile';

interface IProps {
  isProcess: boolean;
  onSubmit: (values: IRegistrationForm, helpers: FormikHelpers<any>) => void;
}

const SignUpForm = React.memo(({ isProcess, onSubmit }: IProps) => {
  const { t } = useTranslation();

  const handleRemoveFile = React.useCallback((formik) => {
    formik.setFieldValue('cv', undefined);
  }, []);

  return (
    <ContentWrapperStyled>
      <Stack spacing={1} textAlign="center">
        <TitleStyled>{t('signup.title')}</TitleStyled>
        <DescriptionStyled>{t('signup.description')}</DescriptionStyled>
      </Stack>

      <Formik
        initialValues={{
          firstname: '',
          lastname: '',
          email: '',
          phone: '',
          message: '',
          cv: undefined,
          accept: false,
        }}
        validationSchema={Yup.object({
          firstname: Yup.string().required(t('general.required')),
          lastname: Yup.string().required(t('general.required')),
          email: Yup.string().email().required(t('general.required')),
          phone: Yup.string()
            .matches(
              /^(\+)?[\d]{9,15}$/,
              'Phone number is not valid. Valid example: +33123456789 or 123456789',
            )
            .required(t('general.required')),
          cv: Yup.mixed().required(t('general.required')),
          message: Yup.string(),
          accept: Yup.boolean().isTrue(t('general.required')),
        })}
        onSubmit={onSubmit}
      >
        {(formik) => (
          <Form>
            <FormWrapperStyled spacing={2}>
              <Stack spacing={1.5}>
                <FormSectionWrapperStyled direction="row" spacing={3}>
                  <FormFieldContainerStyled>
                    <FormTextLabelStyled htmlFor="lastname">
                      {t('general.last_name')}
                    </FormTextLabelStyled>

                    <TextFieldStyled
                      variant="outlined"
                      autoComplete="off"
                      {...formik.getFieldProps('lastname')}
                      error={formik.touched.lastname && Boolean(formik.errors.lastname)}
                      id="lastname"
                      fullWidth
                    />
                  </FormFieldContainerStyled>

                  <FormFieldContainerStyled>
                    <FormTextLabelStyled htmlFor="firstname">
                      {t('general.first_name')}
                    </FormTextLabelStyled>

                    <TextFieldStyled
                      variant="outlined"
                      autoComplete="off"
                      {...formik.getFieldProps('firstname')}
                      error={formik.touched.firstname && Boolean(formik.errors.firstname)}
                      id="firstname"
                      fullWidth
                    />
                  </FormFieldContainerStyled>
                </FormSectionWrapperStyled>

                <FormSectionWrapperStyled direction="row" spacing={3}>
                  <FormFieldContainerStyled>
                    <FormTextLabelStyled htmlFor="email">{t('general.email')}</FormTextLabelStyled>

                    <TextFieldStyled
                      variant="outlined"
                      autoComplete="off"
                      {...formik.getFieldProps('email')}
                      error={formik.touched.email && Boolean(formik.errors.email)}
                      id="email"
                      fullWidth
                    />
                  </FormFieldContainerStyled>

                  <FormFieldContainerStyled>
                    <FormTextLabelStyled htmlFor="phone">{t('general.phone')}</FormTextLabelStyled>

                    <Tooltip
                      title={t('signup.phone_error')}
                      open={!!formik.errors.phone}
                      arrow
                      placement="right"
                      slotProps={{
                        tooltip: {
                          sx: TooltipSx,
                        },
                      }}
                    >
                      <TextFieldStyled
                        variant="outlined"
                        autoComplete="off"
                        {...formik.getFieldProps('phone')}
                        error={formik.touched.phone && Boolean(formik.errors.phone)}
                        id="phone"
                      />
                    </Tooltip>
                  </FormFieldContainerStyled>
                </FormSectionWrapperStyled>

                <FormSectionWrapperStyled spacing={1.5} width="100%">
                  {!formik.values.cv && (
                    <UploadButtonStyled
                      variant="outlined"
                      color="primary"
                      endIcon={<Paperclip2 size={16} />}
                      component="label"
                    >
                      {t('signup.choose_file')}

                      <input
                        type="file"
                        {...formik.getFieldProps('cv')}
                        accept=".pdf,.png,.jpeg,.jpg,.doc,.docx,.zip"
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          const file = e.target.files?.[0];

                          if (file) {
                            formik.setFieldValue('cv', file!);
                          }
                        }}
                        value={undefined}
                      />
                    </UploadButtonStyled>
                  )}

                  {formik.values.cv && (
                    <ChosenFile file={formik.values.cv} onRemove={() => handleRemoveFile(formik)} />
                  )}
                </FormSectionWrapperStyled>

                <FormSectionWrapperStyled spacing={1.5} width="100%">
                  <FormTextLabelStyled htmlFor="message">{t('signup.message')}</FormTextLabelStyled>

                  <TextFieldStyled
                    variant="outlined"
                    autoComplete="off"
                    {...formik.getFieldProps('message')}
                    error={formik.touched.message && Boolean(formik.errors.message)}
                    id="message"
                    multiline
                    rows={4}
                    sx={{ width: '100%' }}
                  />
                </FormSectionWrapperStyled>

                <FormSectionWrapperStyled textAlign="center">
                  <InfoTextStyled>{t('signup.info_1')}</InfoTextStyled>

                  <InfoTextStyled>
                    {t('signup.info_2')}
                    <Link
                      href="mailto:contact@kintpartners.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      contact@kintpartners.com
                    </Link>
                  </InfoTextStyled>

                  <InfoTextStyled>{t('signup.info_3')}</InfoTextStyled>
                </FormSectionWrapperStyled>

                <Stack spacing={3}>
                  <FormControlLabel
                    control={
                      <CheckboxStyled
                        icon={<Record size={24} color="#DEE2E6" />}
                        checkedIcon={<TickCircle size={24} variant="Bold" />}
                        {...formik.getFieldProps('accept')}
                      />
                    }
                    label={t('signup.accept')}
                  />
                </Stack>
              </Stack>

              <Box>
                <ButtonStyled
                  variant="contained"
                  color="primary"
                  disabled={!formik.dirty || !formik.isValid || isProcess}
                  onClick={() => formik.handleSubmit()}
                >
                  {t('signup.submit')}
                </ButtonStyled>
              </Box>
            </FormWrapperStyled>
          </Form>
        )}
      </Formik>
    </ContentWrapperStyled>
  );
});

export default SignUpForm;
